import { Link } from "react-router-dom";
import type { Bairro } from "@/types/bairros";

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" | "bad" }) {
  const color = tone === "bad" ? "text-danger-700" : tone === "warn" ? "text-warning-700" : "text-foreground-900";
  return (
    <div className="rounded-lg bg-background-50 px-3 py-2.5">
      <p className="text-[11px] text-foreground-500 leading-tight">{label}</p>
      <p className={`text-lg font-bold font-heading tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

const n = (v: number | null, suffix = "") =>
  v == null ? "—" : `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}${suffix}`;

export default function BairroDetail({ bairro, onClose }: { bairro: Bairro; onClose: () => void }) {
  const hasTrees = bairro.treeCount > 0;
  const hasTerritorial = bairro.analyzedBlocks > 0;

  return (
    <section className="bg-white rounded-card border border-background-200 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground-900 font-heading truncate">{bairro.name}</h3>
          <p className="text-xs text-foreground-500">
            {bairro.region
              ? `Região ${bairro.region.charAt(0)}${bairro.region.slice(1).toLowerCase()}`
              : "Região não classificada"}
            {bairro.population != null && ` · ${bairro.population.toLocaleString("pt-BR")} habitantes`}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Fechar detalhe"
          className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      <div className="mt-4">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-2">Exemplares arbóreos</h4>
        {hasTrees ? (
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Cadastrados" value={n(bairro.treeCount)} />
            <Stat label="Com vistoria validada" value={n(bairro.assessedCount)} />
            <Stat
              label="Risco alto ou crítico"
              value={n(bairro.highRiskCount)}
              tone={bairro.highRiskCount > 0 ? "bad" : undefined}
            />
            <Stat
              label="Saúde ruim, crítica ou morta"
              value={n(bairro.poorHealthCount)}
              tone={bairro.poorHealthCount > 0 ? "warn" : undefined}
            />
          </div>
        ) : (
          <p className="text-sm text-foreground-500">Nenhum exemplar cadastrado neste bairro ainda.</p>
        )}
        {hasTrees && (
          <Link to="/cta" className="mt-2 inline-block text-xs font-medium text-primary-700 hover:text-primary-800">
            Ver exemplares no CTA
          </Link>
        )}
      </div>

      <div className="mt-5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-2">
          Resiliência territorial
        </h4>
        {hasTerritorial ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Quadras analisadas" value={n(bairro.analyzedBlocks)} />
              <Stat label="Cobertura de copa" value={n(bairro.avgCanopyPercent, "%")} />
              <Stat
                label="Impermeabilização"
                value={n(bairro.avgImperviousPercent, "%")}
                tone={(bairro.avgImperviousPercent ?? 0) >= 70 ? "warn" : undefined}
              />
              <Stat
                label="Ilha de calor alta"
                value={`${n(bairro.highHeatBlocks)} quadras`}
                tone={bairro.highHeatBlocks > 0 ? "bad" : undefined}
              />
              <Stat
                label="Déficit arbóreo alto"
                value={`${n(bairro.highDeficitBlocks)} quadras`}
                tone={bairro.highDeficitBlocks > 0 ? "warn" : undefined}
              />
              <Stat label="Potencial de plantio" value={n(bairro.plantingPotentialCount, " vagas")} />
            </div>
            <p className="mt-2 text-[11px] leading-snug text-foreground-500">
              Estimativa visual preliminar (FITA). Serve para planejar e exige validação em campo.
            </p>
            <Link
              to="/territorio/inspecoes"
              className="mt-1 inline-block text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              Ver inspeções territoriais
            </Link>
          </>
        ) : (
          <p className="text-sm text-foreground-500">
            Nenhuma quadra analisada neste bairro. Os indicadores aparecem após importar inspeções territoriais.
          </p>
        )}
      </div>
    </section>
  );
}
